import { type ActionCreatorBuilder, createAction, type TypeConstant } from 'typesafe-actions';
import { createBoundAction, type UnboundActionCreator } from './actions.js';

export type Requested<Type extends string> = `${Type}_REQUESTED`;
export type Pending<Type extends string> = `${Type}_PENDING`;
export type Fulfilled<Type extends string> = `${Type}_FULFILLED`;
export type Rejected<Type extends string> = `${Type}_REJECTED`;
export type Cancelled<Type extends string> = `${Type}_CANCELLED`;

export type Routine<
  Type extends TypeConstant,
  Meta,
  RequestPayload,
  PendingPayload,
  FulfilledPayload,
  RejectedPayload,
  CancelledPayload
> = {
  requested: ActionCreatorBuilder<Requested<Type>, RequestPayload, Meta>;
  pending: ActionCreatorBuilder<Pending<Type>, PendingPayload, Meta>;
  fulfilled: ActionCreatorBuilder<Fulfilled<Type>, FulfilledPayload, Meta>;
  rejected: ActionCreatorBuilder<Rejected<Type>, RejectedPayload, Meta>;
  cancelled: ActionCreatorBuilder<Cancelled<Type>, CancelledPayload, Meta>;
  REQUESTED: Requested<Type>;
  PENDING: Pending<Type>;
  FULFILLED: Fulfilled<Type>;
  REJECTED: Rejected<Type>;
  CANCELLED: Cancelled<Type>;
};

export type RoutineCreator<Type extends TypeConstant> = <
  Meta,
  RequestPayload,
  PendingPayload,
  FulfilledPayload,
  RejectedPayload,
  CancelledPayload
>() => Routine<Type, Meta, RequestPayload, PendingPayload, FulfilledPayload, RejectedPayload, CancelledPayload>;

/**
 * Implementation
 */
export function createRoutine<Type extends TypeConstant>(type: Type): RoutineCreator<Type> {
  return <Meta, RequestPayload, PendingPayload, FulfilledPayload, RejectedPayload, CancelledPayload>(): Routine<
    Type,
    Meta,
    RequestPayload,
    PendingPayload,
    FulfilledPayload,
    RejectedPayload,
    CancelledPayload
  > => {
    return {
      requested: createAction(`${type}_REQUESTED` as const)<RequestPayload, Meta>(),
      pending: createAction(`${type}_PENDING` as const)<PendingPayload, Meta>(),
      fulfilled: createAction(`${type}_FULFILLED` as const)<FulfilledPayload, Meta>(),
      rejected: createAction(`${type}_REJECTED` as const)<RejectedPayload, Meta>(),
      cancelled: createAction(`${type}_CANCELLED` as const)<CancelledPayload, Meta>(),
      REQUESTED: `${type}_REQUESTED` as const,
      PENDING: `${type}_PENDING` as const,
      FULFILLED: `${type}_FULFILLED` as const,
      REJECTED: `${type}_REJECTED` as const,
      CANCELLED: `${type}_CANCELLED` as const
    };
  };
}

export type BoundRoutine<
  Type extends TypeConstant,
  Meta,
  RequestPayload,
  PendingPayload,
  FulfilledPayload,
  RejectedPayload,
  CancelledPayload
> = {
  requested: UnboundActionCreator<Requested<Type>, Meta, RequestPayload>;
  pending: UnboundActionCreator<Pending<Type>, Meta, PendingPayload>;
  fulfilled: UnboundActionCreator<Fulfilled<Type>, Meta, FulfilledPayload>;
  rejected: UnboundActionCreator<Rejected<Type>, Meta, RejectedPayload>;
  cancelled: UnboundActionCreator<Cancelled<Type>, Meta, CancelledPayload>;
  REQUESTED: Requested<Type>;
  PENDING: Pending<Type>;
  FULFILLED: Fulfilled<Type>;
  REJECTED: Rejected<Type>;
  CANCELLED: Cancelled<Type>;
};

export type BoundRoutineCreator<Type extends TypeConstant> = <
  Meta,
  RequestPayload,
  PendingPayload,
  FulfilledPayload,
  RejectedPayload,
  CancelledPayload
>() => BoundRoutine<Type, Meta, RequestPayload, PendingPayload, FulfilledPayload, RejectedPayload, CancelledPayload>;

export function createBoundRoutine<Type extends TypeConstant>(type: Type): BoundRoutineCreator<Type> {
  return <Meta, RequestPayload, PendingPayload, FulfilledPayload, RejectedPayload, CancelledPayload>(): BoundRoutine<
    Type,
    Meta,
    RequestPayload,
    PendingPayload,
    FulfilledPayload,
    RejectedPayload,
    CancelledPayload
  > => {
    return {
      requested: createBoundAction(`${type}_REQUESTED` as const)<Meta, RequestPayload>(),
      pending: createBoundAction(`${type}_PENDING` as const)<Meta, PendingPayload>(),
      fulfilled: createBoundAction(`${type}_FULFILLED` as const)<Meta, FulfilledPayload>(),
      rejected: createBoundAction(`${type}_REJECTED` as const)<Meta, RejectedPayload>(),
      cancelled: createBoundAction(`${type}_CANCELLED` as const)<Meta, CancelledPayload>(),
      REQUESTED: `${type}_REQUESTED` as const,
      PENDING: `${type}_PENDING` as const,
      FULFILLED: `${type}_FULFILLED` as const,
      REJECTED: `${type}_REJECTED` as const,
      CANCELLED: `${type}_CANCELLED` as const
    };
  };
}
